"use client";

import { useEffect, useState, FC, useMemo } from "react";
import { useRouter } from "next/navigation";
import { BreadcrumbLink } from "@/hooks/useBreadcrumbLinks";
import { capitalize } from "lodash";
import { ProductData, ProductVariant, Image } from "@/lib/5874/types";
import { ProductAvailability, ProductCondition } from "@/lib/bigcommerce/enums";
import moment from "moment";
import useProductPricing from "@/hooks/useProductPricing";

const SchemaMarkupType = {
	Breadcrumbs: "BreadcrumbList",
	Product: "Product",
	BlogPost: "NewsArticle",
} as const;

export type BlogSchemaData = {
	title: string;
	datePublished?: Date;
	dateModified?: Date;
};

export const BlogSchemaMarkup: FC<{ data?: BlogSchemaData }> = ({ data }) => {
	const [jsonLd, setJsonLd] = useState<string>("");

	const getBlogPostProperties = (data?: BlogSchemaData) => {
		if (!data) {
			return undefined;
		}

		const properties: any = {
			headline: data.title,
		};

		if (data.datePublished) {
			properties.datePublished = data.datePublished;
		}

		if (data.dateModified) {
			properties.dateModified = data.dateModified;
		}

		return properties;
	};

	useEffect(() => {
		const innerData = getBlogPostProperties(data);

		if (!innerData) {
			return;
		}

		const structuredData = {
			"@context": "https://schema.org",
			"@type": SchemaMarkupType.BlogPost,
			...innerData,
		};
		const nextJsonLd = JSON.stringify(structuredData);
		setJsonLd(nextJsonLd);
	}, [data]);

	return (
		<section>
			<script
				id='blog-schema'
				type='application/ld+json'
				dangerouslySetInnerHTML={{ __html: jsonLd }}
			/>
		</section>
	);
};

export const BreadcrumbMarkup: React.FC<{ breadcrumbs?: BreadcrumbLink[] }> = ({
	breadcrumbs,
}) => {
	const router = useRouter();
	const [jsonLd, setJsonLd] = useState<string>("");

	const getItemListElements = (breadcrumbs: BreadcrumbLink[]) => {
		if (typeof window === "undefined") {
			return;
		}
		// Get the current origin more reliably
		const origin = typeof window !== "undefined" ? window.location.origin : "";
		return breadcrumbs.map((breadcrumb, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: capitalize(breadcrumb.text),
			item: `${origin}${breadcrumb.href}`,
		}));
	};
	useEffect(() => {
		if (breadcrumbs) {
			const structuredData = {
				"@context": "https://schema.org",
				"@type": SchemaMarkupType.Breadcrumbs,
				itemListElement: getItemListElements(breadcrumbs),
			};
			const nextJsonLd = JSON.stringify(structuredData);
			setJsonLd(nextJsonLd);
		}
	}, [breadcrumbs]);

	if (!jsonLd) {
		return null;
	}

	return (
		<section>
			<script
				type='application/ld+json'
				dangerouslySetInnerHTML={{ __html: jsonLd }}
			/>
		</section>
	);
};

export const ProductSchemaMarkup: FC<{
	product?: ProductData;
	selectedVariant?: ProductVariant;
}> = ({ product, selectedVariant }) => {
	const { getProductOrVariantPricing } = useProductPricing();

	const productPricing = useMemo(() => {
		if (!product) {
			return undefined;
		}

		if (selectedVariant) {
			return getProductOrVariantPricing(selectedVariant);
		}

		return getProductOrVariantPricing(product);
	}, [product, selectedVariant, getProductOrVariantPricing]);

	const [jsonLd, setJsonLd] = useState<string>();

	const getItemCondition = (condition: ProductCondition) => {
		switch (condition) {
			case "NEW":
				return "https://schema.org/NewCondition";
			case "REFURBISHED":
				return "https://schema.org/RefurbishedCondition";
			case "USED":
				return "https://schema.org/UsedCondition";
			default:
				break;
		}
	};

	const getAvailability = (availability: ProductAvailability) => {
		switch (availability) {
			case ProductAvailability.AVAILABLE:
				return "https://schema.org/InStock";
			case ProductAvailability.PREORDER:
				return "https://schema.org/PreOrder";
			case ProductAvailability.UNAVAILABLE:
				return "https://schema.org/OutOfStock";
			default:
				break;
		}
	};

	const getProductProperties = (product: ProductData) => {
		const itemCondition = getItemCondition(product.condition);
		const availability = getAvailability(product.availability);

		return {
			name: product.name,
			image: product.media.images.map((i) => Object.values(i.url)).flat(),
			description: product.plainTextDescription,
			sku: product.sku,
			...(product.mpn && { mpn: product.mpn }),
			...(product.brand?.name && {
				brand: { "@type": "Brand", name: product.brand?.name },
			}),
			...(productPricing?.incVat && {
				offers: {
					"@type": "Offer",
					url: typeof window !== "undefined" ? window.location.href : "",
					priceCurrency: productPricing.incVat.currencyCode,
					price: productPricing.incVat.price,
					priceValidUntil: moment().add(7, "days").format("YYYY-MM-DD"),
					...(itemCondition && { itemCondition }),
					...(availability && { availability }),
					hasMerchantReturnPolicy: {
						"@type": "MerchantReturnPolicy",
						applicableCountry: "GB",
						returnPolicyCategory:
							"https://schema.org/MerchantReturnFiniteReturnWindow",
					},
				},
			}),
			hasMerchantReturnPolicy: {
				"@type": "MerchantReturnPolicy",
				applicableCountry: "GB",
				returnPolicyCategory:
					"https://schema.org/MerchantReturnFiniteReturnWindow",
			},
		};
	};

	useEffect(() => {
		if (!product) {
			jsonLd && setJsonLd(undefined);
			return;
		}

		const structuredData = {
			"@context": "https://schema.org",
			"@type": SchemaMarkupType.Product,
			...getProductProperties(product),
		};
		const nextJsonLd = JSON.stringify(structuredData);
		setJsonLd(nextJsonLd);
	}, [product]);

	if (!jsonLd) {
		return null;
	}

	return (
		<section>
			<script
				type='application/ld+json'
				dangerouslySetInnerHTML={{ __html: jsonLd }}
			/>
		</section>
	);
};
